import { ChevronDown } from "lucide-react";
import { forwardRef, useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Accessible disclosure (WAI-ARIA pattern): a native button with `aria-expanded` controlling a
 * labelled region. Enter and Space toggle it because it is a real button. Children render only
 * while open, so closed panels fetch nothing.
 */
export const Disclosure = forwardRef<
  HTMLDivElement,
  { open: boolean; onOpenChange: (open: boolean) => void; header: ReactNode; children: ReactNode; className?: string }
>(function Disclosure({ open, onOpenChange, header, children, className }, ref) {
  const id = useId();
  const buttonId = `${id}-button`;
  const panelId = `${id}-panel`;
  return (
    <div ref={ref} className={cn("rounded-lg border border-border bg-surface shadow-card", className)}>
      <h3 className="m-0 text-base">
        <button
          id={buttonId}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => onOpenChange(!open)}
          className="flex min-h-11 w-full items-start justify-between gap-4 rounded-lg px-4 py-4 text-left hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:px-6"
        >
          <span className="flex-1">{header}</span>
          <ChevronDown aria-hidden="true" className={cn("mt-1 h-5 w-5 shrink-0 text-fg-muted transition-transform", open && "rotate-180")} />
        </button>
      </h3>
      <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!open} className="border-t border-border px-4 py-4 sm:px-6">
        {open ? children : null}
      </div>
    </div>
  );
});
